import React, { useCallback, useEffect, useRef } from 'react';
import { css } from '@emotion/css';
import classnames from 'classnames';
import { Indicator, Modal, Notification } from 'is24-corecss';
import { IconS24Checkmark24, IconS24Gift24 } from '@is24/cosma-ui-icons';
import SVGWohnenPlusLight from './SVGWohnenPlusLight';
import SVGSuccessAnimation from './SVGSuccessAnimation';
import { useWohnenPlusLightUpsellPurchase } from './useWohnenPlusLightUpsellPurchase.hook';
import { PlusBadge } from '@/components/common/display/plusBadge/PlusBadge';
import { default as Msg } from '@/i18n/components/FormattedMessage';
import { useBodyScroll } from '@/customHooks/useBodyScroll.hook';
import { useIntl } from '@/i18n/hooks/useIntl';
import { useGetUserAccountDetails } from '@/apiHooks/userAccount.hook';
import { report } from '@/tracking/tracking';
import { TrackingEvents } from '@/tracking/trackingEvents';

const styles = {
  badge: css`
    margin-bottom: var(--size-300);
  `,
  checkList: css`
    list-style: none;
    gap: var(--size-200);
  `,
  checkListItem: css`
    gap: var(--size-100);
    align-items: center;
  `,
  fadeIn: css`
    @keyframes fadeSlideIn {
      from {
        opacity: 0;
        transform: translateY(8px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
    animation: fadeSlideIn 0.4s ease-in-out;
  `,
  flexColumn: css`
    flex-direction: column;
  `,
} as const;

interface WohnenPlusLightUpsellModalProps {
  onClose: () => void;
}

export const WohnenPlusLightUpsellModal = ({ onClose }: WohnenPlusLightUpsellModalProps) => {
  const { t } = useIntl();
  const hasReportedPageView = useRef(false);
  const { userAccountDetails } = useGetUserAccountDetails();

  const handlePurchaseSuccess = useCallback(() => {
    report(TrackingEvents.WOHNEN_PLUS_LIGHT.SUBSCRIPTION_SALE, false);
    report(TrackingEvents.WOHNEN_PLUS_LIGHT.SUCCESS, false);
  }, []);

  const { handleBuyNow, isLoading, isSuccess, isError, resetError } =
    useWohnenPlusLightUpsellPurchase(handlePurchaseSuccess);

  const handleCloseX = useCallback(() => {
    report(TrackingEvents.WOHNEN_PLUS_LIGHT.MODAL_CLOSE_X, false);
    onClose();
  }, [onClose]);

  const handleCloseDecline = useCallback(() => {
    report(TrackingEvents.WOHNEN_PLUS_LIGHT.MODAL_CLOSE_REFRAIN, false);
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (!hasReportedPageView.current) {
      hasReportedPageView.current = true;
      report(TrackingEvents.WOHNEN_PLUS_LIGHT.PAGE_VISITED, false);
    }
  }, []);

  useBodyScroll(true);

  const welcomeText = userAccountDetails?.firstName
    ? t({ id: 'welcomeModal.wohnenPlusLight.success.title' }, { FirstName: userAccountDetails.firstName })
    : t({ id: 'welcomeModal.wohnenPlusLight.success.titleNoName' });

  return (
    <Modal
      visible
      title=' '
      onClose={handleCloseX}
      {...(!isSuccess && {
        primaryFooterBtn: (
          <Modal.FooterPrimaryBtn
            label={t({ id: 'welcomeModal.wohnenPlusLight.cta.buyNow' })}
            onClick={handleBuyNow}
            loading={isLoading}
          />
        ),
        secondaryFooterBtn: (
          <Modal.FooterSecondaryBtn
            label={t({ id: 'welcomeModal.wohnenPlusLight.cta.decline' })}
            onClick={handleCloseDecline}
          />
        ),
      })}
    >
      {isError && (
        <Notification
          type='error'
          variant='flat'
          title={t({ id: 'welcomeModal.wohnenPlusLight.error.title' })}
          className='margin-bottom-m'
          message={t({ id: 'welcomeModal.wohnenPlusLight.error.message' })}
          onClose={resetError}
        />
      )}
      <div key={String(isSuccess)} className={css(styles.fadeIn)}>
        {isSuccess ? (
          <div
            className={classnames(
              'display-flex grid-align-center text-center padding-vertical-xl',
              css(styles.flexColumn),
            )}
          >
            <SVGSuccessAnimation />
            <h3 className='font-heading-small-bold margin-top-l margin-bottom-m'>{welcomeText}</h3>
            <p className='font-body-medium-regular'>{t({ id: 'welcomeModal.wohnenPlusLight.success.subtitle' })}</p>
          </div>
        ) : (
          <>
            <Indicator
              appearance='label'
              labelSize='medium'
              labelColor='green'
              labelVariant='flat'
              leftIcon={<IconS24Gift24 width={16} height={16} />}
              className={css(styles.badge)}
            >
              {t({ id: 'welcomeModal.wohnenPlusLight.badge' })}
            </Indicator>
            <div className='display-flex margin-bottom-m'>
              <div>
                <div className='font-heading-large-bold margin-bottom-m'>
                  <Msg
                    id='welcomeModal.wohnenPlusLight.headline'
                    defaultMessage='Save money with Wohnen+ Light'
                    values={{ WohnenPlusLogo: <PlusBadge variant='living' /> }}
                  />
                </div>
                <div className='font-body-medium-regular '>{t({ id: 'welcomeModal.wohnenPlusLight.subheadline' })}</div>
              </div>
              <div>
                <SVGWohnenPlusLight />
              </div>
            </div>
            <ul className={classnames(`display-flex flex-column`, css(styles.checkList))}>
              <li className={classnames('display-flex font-body-medium-regular', styles.checkListItem)}>
                <IconS24Checkmark24 width={16} height={16} className='flex-shrink-0' />
                {t({ id: 'welcomeModal.wohnenPlusLight.benefit1' })}
              </li>
              <li className={classnames('display-flex font-body-medium-regular', styles.checkListItem)}>
                <IconS24Checkmark24 width={16} height={16} className='flex-shrink-0' />
                {t({ id: 'welcomeModal.wohnenPlusLight.benefit2' })}
              </li>
              <li className={classnames('display-flex font-body-medium-regular', styles.checkListItem)}>
                <IconS24Checkmark24 width={16} height={16} className='flex-shrink-0' />
                {t({ id: 'welcomeModal.wohnenPlusLight.benefit3' })}
              </li>
              <li className={classnames('display-flex font-body-medium-regular', styles.checkListItem)}>
                <IconS24Checkmark24 width={16} height={16} className='flex-shrink-0' />
                {t({ id: 'welcomeModal.wohnenPlusLight.benefit4' })}
              </li>
              <li className={classnames('display-flex font-body-medium-regular', styles.checkListItem)}>
                <IconS24Checkmark24 width={16} height={16} className='flex-shrink-0' />
                {t({ id: 'welcomeModal.wohnenPlusLight.benefit5' })}
              </li>
            </ul>

            <div className='font-body-small-regular color-text-weak margin-top-xxl'>
              {t({ id: 'welcomeModal.wohnenPlusLight.disclaimer' })}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default WohnenPlusLightUpsellModal;
